import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSupplierService } from 'services/suppliers';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Supplier } from 'types/supplier';

const SUPPLIERS_CACHE_KEY_BASE = 'suppliers_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const getSuppliersCacheKey = (
  includeVoided: boolean,
  companyId: string | null | undefined,
) => {
  const baseKey = `${SUPPLIERS_CACHE_KEY_BASE}_${includeVoided ? 'withVoided' : 'withoutVoided'}`;
  return getCacheKeyWithCompany(baseKey, companyId);
};

const sortSuppliersByDateDesc = (suppliers: Supplier[]) =>
  [...suppliers].sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseSuppliersDataOptions {
  includeVoided: boolean;
}

interface UseSuppliersDataReturn {
  suppliers: Supplier[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  addSupplier: (supplier: Supplier) => void;
  updateSupplier: (supplier: Supplier) => void;
  removeSupplier: (id: string) => void;
}

export function useSuppliersData(
  options: UseSuppliersDataOptions,
): UseSuppliersDataReturn {
  const { includeVoided } = options;
  const supplierService = useSupplierService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const suppliersCacheKey = useMemo(
    () => getSuppliersCacheKey(includeVoided, activeCompanyId),
    [includeVoided, activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedSuppliers,
    setData: setCachedSuppliers,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Supplier[]>({
    key: suppliersCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  // Reset cache check when includeVoided or company changes (cache key changes)
  useEffect(() => {
    setCacheChecked(false);
    setLoading(true);
    // Clear suppliers temporarily while loading new cache
    setSuppliers([]);
    // Reset error state
    setError(null);
  }, [includeVoided, activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready

    if (cachedSuppliers) {
      // Use cached data - it's still valid (hook already checked expiration)
      setSuppliers(sortSuppliersByDateDesc(cachedSuppliers));
      setLoading(false);
      setCacheChecked(true);
    } else {
      // No cache or expired - mark as checked so we can fetch
      setCacheChecked(true);
    }
  }, [
    cachedSuppliers,
    cacheInitialized,
    isCompanyInitialized,
    suppliersCacheKey,
  ]);

  const fetchSuppliers = useCallback(
    async (isReload = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const fetchedSuppliers =
          await supplierService.getAllSuppliers(includeVoided);
        const sortedSuppliers = sortSuppliersByDateDesc(fetchedSuppliers);
        setSuppliers(sortedSuppliers);

        // Limit cached suppliers to first 50 to avoid large data issues
        const suppliersToCache =
          sortedSuppliers.length > 50
            ? sortedSuppliers.slice(0, 50)
            : sortedSuppliers;

        // Save to cache with encryption
        await setCachedSuppliers(suppliersToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch suppliers:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load suppliers. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      includeVoided,
      supplierService,
      setCachedSuppliers,
      isCompanyInitialized,
      activeCompanyId,
    ],
  );

  // Only fetch data if cache is checked, company is initialized, and no cached data exists
  useEffect(() => {
    if (
      cacheChecked &&
      cacheInitialized &&
      isCompanyInitialized &&
      !cachedSuppliers
    ) {
      void fetchSuppliers();
    }
  }, [
    cacheChecked,
    cacheInitialized,
    isCompanyInitialized,
    cachedSuppliers,
    fetchSuppliers,
    includeVoided,
  ]);

  const reload = useCallback(() => {
    void fetchSuppliers(true);
  }, [fetchSuppliers]);

  const addSupplier = useCallback(
    (newSupplier: Supplier) => {
      setSuppliers((prevSuppliers) => {
        const nextSuppliers = [newSupplier, ...prevSuppliers];
        const sortedSuppliers = sortSuppliersByDateDesc(nextSuppliers);

        // Update cache with encrypted data (fire and forget)
        void setCachedSuppliers(sortedSuppliers);

        return sortedSuppliers;
      });
    },
    [setCachedSuppliers],
  );

  const updateSupplier = useCallback(
    (updatedSupplier: Supplier) => {
      setSuppliers((prevSuppliers) => {
        const nextSuppliers = prevSuppliers.map((supplier) =>
          supplier.id === updatedSupplier.id ? updatedSupplier : supplier,
        );

        const sortedSuppliers = sortSuppliersByDateDesc(nextSuppliers);

        // Update cache with encrypted data (fire and forget)
        void setCachedSuppliers(sortedSuppliers);

        return sortedSuppliers;
      });
    },
    [setCachedSuppliers],
  );

  const removeSupplier = useCallback(
    (id: string) => {
      setSuppliers((prevSuppliers) => {
        const nextSuppliers = prevSuppliers.filter(
          (supplier) => supplier.id !== id,
        );

        // Update cache with encrypted data (fire and forget)
        void setCachedSuppliers(nextSuppliers);

        return nextSuppliers;
      });
    },
    [setCachedSuppliers],
  );

  return {
    suppliers,
    loading,
    reloading,
    error,
    reload,
    addSupplier,
    updateSupplier,
    removeSupplier,
  };
}
